<template>
  <keep-alive>
    <header>
      <Grid variant="detail" class="article-hero" :class="{ 'has--margin-top': !hasMedia }">
        <div
          v-if="hasMedia"
          class="is--full-detail-width-small is--full-detail-width-medium is--full-detail-width-large"
        >
          <ContentImages
            v-if="heroImage"
            :image="heroImage"
            :show-caption="false"
            :caption-has-padding="true"
            priority="high"
            loading="eager"
            :_index="0"
          />

          <ContentImages
            v-if="image?.image && !embed?.item?.embed && !heroImage"
            v-bind="image"
            :show-caption="true"
            :caption-has-padding="true"
            priority="high"
            loading="eager"
            :_index="0"
          />

          <ContentEmbeds
            v-if="embed?.item?.embed"
            :static-data="embed"
            :caption-has-padding="true"
            :caption="embed.item.caption"
            :show-caption="!hideMediaDescription"
          />
        </div>
        <div :class="['article-hero-heading-content', variantClasses]">
          <div v-if="state.dossiers?.length" class="article-hero-dossiers-list">
            <Button
              v-for="dossier in state.dossiers"
              :key="dossier?.label || dossier?.title"
              type="label"
              :label="dossier?.label || dossier?.title"
              :to="`/dossiers/${dossier?.slug}`"
              icon="eenvandaag-general:copy-04"
              :analytics="{ linkAnalyticsTag: 'article-hero' }"
            />
          </div>
          <Heading
            v-if="props.title"
            :level="1"
            :content="props.title"
            class="article-hero-title"
          />
          <MetaList
            v-if="hasMeta"
            :authors="props.authors"
            :publish-time="hidePublish ? undefined : props.publishTime"
            :adjust-time="hidePublish ? undefined : props.adjustTime"
            :article-type="props.articleType"
            :analytics="{ linkAnalyticsTag: 'article-hero' }"
          />
          <div v-if="hasButtons" class="article-hero-buttons">
            <template v-if="props.showShareButton">
              <Share
                :title="props.title"
                :url="url"
                :analytics="{ linkAnalyticsTag: 'article-hero' }"
              />
              <GoogeFavorite :analytics="{ linkAnalyticsTag: 'article-hero' }" />
            </template>
            <Button
              v-if="props.socialAccountX"
              :to="`https://x.com/${props.socialAccountX}`"
              target="_blank"
              label="Volgen"
              variant="secondary"
              icon="eenvandaag-general:x-small"
              :analytics="{ linkAnalyticsTag: 'article-hero' }"
            />
            <a v-if="props.email" :href="`mailto:${props.email}`">
              <Button
                label="Mailen"
                variant="secondary"
                icon="eenvandaag-general:envelope"
                :analytics="{ linkAnalyticsTag: 'article-hero' }"
              />
            </a>
            <Tolkie
              v-if="tolkieEnabled && showTolkie && props.id === currentPage?.id"
              :features="tolkieFeatures"
            />
          </div>
        </div>
        <Alinea
          v-if="description?.blocks"
          :blocks="description.blocks"
          :variant="heroDescriptionVariant"
          :heading-level="2"
        />
      </Grid>
    </header>
  </keep-alive>
</template>

<script setup lang="ts">
import type {
  IArticlesArticle,
  ITextBlocks,
  TArticle,
  TTextContent,
  IEmbed,
} from '~~/types/local_types';
import MetaList from '~/components/MetaList.vue';
import type { File as IDirectusFiles } from '@directus/types';
import usePageStore from '~/stores/page';
import { storeToRefs } from 'pinia';

const pageStore = usePageStore();
const { currentPage } = storeToRefs(pageStore);
const { tolkieConfig } = useRuntimeConfig().public;
const tolkieEnabled = tolkieConfig.tolkie?.enabled;

// Needed voor tolkie
const tolkieFeatures = [
  'tolkie-tts-button',
  'tolkie-summary-button',
  'tolkie-translate-button',
  'tolkie-fontsize-button',
  'tolkie-dyslexia-font-button',
  'tolkie-tool-button',
];

interface IProps extends IArticlesArticle {
  showMeta?: boolean;
  showShareButton?: boolean;
  heroImage?: IDirectusFiles;
  heroDescription?: ITextBlocks;
  heroDescriptionVariant?: TTextContent;
  variant?: 'narrow' | 'wide';
  socialAccountX?: string;
  email?: string;
  tolkie?: string[];
  tolkieButtons?: string[];
}

const props = withDefaults(defineProps<IProps>(), {
  showMeta: true,
  showShareButton: true,
  heroImage: undefined,
  heroDescription: undefined,
  heroDescriptionVariant: 'intro',
  variant: 'wide',
  socialAccountX: undefined,
  email: undefined,
  tolkie: undefined,
  tolkieButtons: undefined,
});

const showTolkie = props.tolkieButtons?.[0] === 'enabled';

const image = computed(() => {
  return props.mainImage?.map((item) => item.contentImagesId)[0];
});
const embed = computed(() => {
  return props.mainEmbed?.map((item) => ({ item: item.contentEmbedsId }))[0];
});

const hidePublish = computed(() => {
  return props.articleType === 'peilingtrends';
});

const state = reactive({
  dossiers: props.dossiers?.map((dossier) => dossier.dossiersId!) || [],
});

const hasMedia = computed(() => props.heroImage || embed.value?.item?.embed || image.value?.image);

const description = computed(() => {
  // if there is a heroDescription, return that, else return text.text.blocks. Else, return undefined
  return props.heroDescription || (props.intro as ITextBlocks | undefined) || undefined;
});

const hideMediaDescription = computed(() => {
  const { oembed } = embed.value?.item?.embed as IEmbed;
  return oembed?.type === 'video';
});

const hasMeta = computed(
  () => props.showMeta && (props.authors || props.publishTime || props.articleType),
);

const hasButtons = computed(() => props.socialAccountX || props.email || props.showShareButton);
const { url } = useArticleData(props as never as TArticle);

const variantClasses = computed(() => {
  return {
    // Currently only disabled, just in case they want it back
    //
    // 'is--full-detail-width-medium': props.variant === 'wide',
    // 'is--full-detail-width-large': props.variant === 'wide',
    // 'has--padding': props.variant === 'wide',
  };
});
</script>

<style scoped lang="postcss">
.article-hero {
  /* this fixes a jumpy hydration issue */
  transition: margin-top 0.25s 0.25s ease;

  &.has--margin-top {
    margin-top: var(--grid-margin-top-default-large);
  }

  @media (--lg-viewport) {
    --grid-margin-top-default-large: calc(var(--base-spacing) * 10);
  }
}

.article-hero-title {
  @add-mixin heading-3xl;

  color: hsl(var(--color-primary-950));
}

.article-hero-heading-content {
  display: flex;
  flex-direction: column;
  gap: var(--unit-16);

  @media (--md-viewport) {
    padding-top: var(--unit-8);
    padding-bottom: var(--unit-8);
  }
}

.article-hero-dossiers-list {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: var(--unit-8);
}

.article-hero-buttons {
  display: inline;
  margin-bottom: calc(var(--unit-8) * -1);

  > * {
    margin-right: var(--unit-8);
    margin-bottom: var(--unit-8);
  }
}
</style>
